import isString from 'lodash/isString';
import isPlainObject from 'lodash/isPlainObject';

/**
 * A Exchange message with optional payload and metadata properties.
 */
export interface ExchangeMessage<
  Type extends string = string,
  Payload = undefined,
  Meta = undefined
> {
  /**
   * The `type` of an message identifies to the consumer the nature of the message that has occurred.
   * Two messages with the same `type` MUST be strictly equivalent (using `===`)
   */
  type: Type;
  /**
   * The optional `payload` property MAY be any type of value.
   * It represents the payload of the message.
   * Any information about the message that is not the type or status of the message should be part of the `payload` field.
   * By convention, if `error` is `true`, the `payload` SHOULD be an error object.
   * This is akin to rejecting a promise with an error object.
   */
  payload?: Payload;
  /**
   * The optional `error` property MAY be set to true if the message represents an error.
   * An message whose `error` is true is analogous to a rejected Promise.
   * By convention, the `payload` SHOULD be an error object.
   * If `error` has any other value besides `true`, including `undefined`, the message MUST NOT be interpreted as an error.
   */
  error?: boolean;
  /**
   * The optional `meta` property MAY be any type of value.
   * It is intended for any extra information that is not part of the payload.
   */
  meta?: Meta;
  /**
   * The optional `txId` property identified to the consumer the unique
   * identifier of the current message transaction, where a transaction may
   * contain a related outgoing and incoming message (request/response)
   */
  txId?: string;
}

/**
 * An extension of the Exchange message that represents an message containing an error as its payload.
 */
export interface ErrorExchangeMessage<
  Type extends string = string,
  CustomError extends NativeMessageExchangeError = NativeMessageExchangeError,
  Meta = undefined
> extends ExchangeMessage<Type, CustomError, Meta> {
  /**
   * The required `error` property MUST be set to `true` if the message represents an error.
   */
  error: true;
}

/**
 * Alias for ExchangeMessage.
 */
export type EM<
  Type extends string = string,
  Payload = undefined,
  Meta = undefined
> = ExchangeMessage<Type, Payload, Meta>;

/**
 * Alias for ErrorExchangeMessage.
 */
export type ErrorEM<
  CustomError extends NativeMessageExchangeError = NativeMessageExchangeError,
  Meta = undefined,
  Type extends string = string
> = ErrorExchangeMessage<Type, CustomError, Meta>;

/**
 * Returns `true` if `message` is EM compliant.
 */
export function isEM<
  Type extends string = string,
  Payload = undefined,
  Meta = undefined
>(message: ExchangeMessage<Type, Payload, Meta>): boolean {
  return (
    isPlainObject(message) &&
    isString(message.type) &&
    Object.keys(message).every(isValidKey)
  );
}

/**
 * Returns `true` if `message` is EM compliant error.
 */
export function isError<
  Type extends string = string,
  CustomError extends NativeMessageExchangeError = NativeMessageExchangeError,
  Meta = undefined
>(message: ErrorExchangeMessage<Type, CustomError, Meta>): boolean {
  return isEM(message) && message.error === true;
}

export interface ExchangeMessageWithTransactionId<
  Type extends string = string,
  Payload = undefined,
  Meta = undefined
> extends ExchangeMessage<Type, Payload, Meta> {
  /**
   * The required `txId` property identified to the consumer the unique
   * identifier of the current message transaction, where a transaction may
   * contain a related outgoing and incoming message (request/response)
   */
  txId: string;
}
export type EMWithTransactionId<
  Type extends string = string,
  Payload = undefined,
  Meta = undefined
> = ExchangeMessageWithTransactionId<Type, Payload, Meta>;

/**
 * A Exchange message with a required payload property.
 */
export interface ExchangeMessageWithPayload<
  Type extends string = string,
  Payload = undefined,
  Meta = undefined
> extends ExchangeMessage<Type, Payload, Meta> {
  /**
   * The required `payload` property MAY be any type of value.
   * It represents the payload of the message.
   * Any information about the message that is not the type or status of the message should be part of the `payload` field.
   * By convention, if `error` is `true`, the `payload` SHOULD be an error object.
   * This is akin to rejecting a promise with an error object.
   */
  payload: Payload;
}
/**
 * Alias for EMWithPayload
 */
export type EMWithPayload<
  Type extends string = string,
  Payload = undefined,
  Meta = undefined
> = ExchangeMessageWithPayload<Type, Payload, Meta>;

/**
 * A Exchange message with a required metadata property.
 */
export interface ExchangeMessageWithMeta<
  Type extends string = string,
  Payload = undefined,
  Meta = undefined
> extends ExchangeMessage<Type, Payload, Meta> {
  /**
   * The required `meta` property MAY be any type of value.
   * It is intended for any extra information that is not part of the payload.
   */
  meta: Meta;
}
/**
 * Alias for ExchangeMessageWithMeta
 */
export type EMWithMeta<
  Type extends string = string,
  Payload = undefined,
  Meta = undefined
> = ExchangeMessageWithMeta<Type, Payload, Meta>;

/**
 * A Exchange message with required payload and metadata properties.
 */
export type ExchangeMessageWithPayloadAndMeta<
  Type extends string = string,
  Payload = undefined,
  Meta = undefined
> = ExchangeMessageWithPayload<Type, Payload, Meta> &
  ExchangeMessageWithMeta<Type, Payload, Meta>;
/**
 * Alias for ExchangeMessageWithPayloadAndMeta
 */
export type EMWithPayloadAndMeta<
  Type extends string = string,
  Payload = undefined,
  Meta = undefined
> = ExchangeMessageWithPayloadAndMeta<Type, Payload, Meta>;

/**
 * A Exchange message with inferred requirements for the payload and metadata properties.
 * The `payload` and `meta` properties will be required if the corresponding type argument
 * if not the `undefined` type.
 */
export type ExchangeMessageAuto<
  Type extends string = string,
  Payload = undefined,
  Meta = undefined
> = (Payload extends undefined
  ? Meta extends undefined
    ? ExchangeMessage<Type, Payload, Meta>
    : ExchangeMessageWithMeta<Type, Payload, Meta>
  : Meta extends undefined
  ? ExchangeMessageWithPayload<Type, Payload, Meta>
  : ExchangeMessageWithPayloadAndMeta<Type, Payload, Meta>) &
  ExchangeMessageWithTransactionId<Type, Payload, Meta>;
/**
 * Alias for ExchangeMessageAuto
 */
export type EMAuto<
  Type extends string = string,
  Payload = undefined,
  Meta = undefined
> = ExchangeMessageAuto<Type, Payload, Meta>;

/**
 * A Exchange Error Message with a required payload property.
 */
export type ErrorExchangeMessageWithPayload<
  Type extends string = string,
  CustomError extends NativeMessageExchangeError = NativeMessageExchangeError,
  Meta = undefined
> = ErrorExchangeMessage<Type, CustomError, Meta> &
  ExchangeMessageWithPayload<Type, CustomError, Meta>;
/**
 * Alias for ErrorExchangeMessageWithPayload
 */
export type ErrorEMWithPayload<
  Type extends string = string,
  CustomError extends NativeMessageExchangeError = NativeMessageExchangeError,
  Meta = undefined
> = ErrorExchangeMessageWithPayload<Type, CustomError, Meta>;

/**
 * A Exchange Error Message with a required metadata property.
 */
export type ErrorExchangeMessageWithMeta<
  Type extends string = string,
  CustomError extends NativeMessageExchangeError = NativeMessageExchangeError,
  Meta = undefined
> = ErrorExchangeMessage<Type, CustomError, Meta> &
  ExchangeMessageWithMeta<Type, CustomError, Meta>;
/**
 * Alias for ErrorExchangeMessageWithMeta
 */
export type ErrorEMWithMeta<
  Type extends string = string,
  CustomError extends NativeMessageExchangeError = NativeMessageExchangeError,
  Meta = undefined
> = ErrorExchangeMessageWithMeta<Type, CustomError, Meta>;

/**
 * A Exchange Error Message with required payload and metadata properties.
 */
export type ErrorExchangeMessageWithPayloadAndMeta<
  Type extends string = string,
  CustomError extends NativeMessageExchangeError = NativeMessageExchangeError,
  Meta = undefined
> = ErrorExchangeMessageWithPayload<Type, CustomError, Meta> &
  ErrorExchangeMessageWithMeta<Type, CustomError, Meta>;
/**
 * Alias for ErrorExchangeMessageWithPayloadAndMeta
 */
export type ErrorEMWithPayloadAndMeta<
  Type extends string = string,
  CustomError extends NativeMessageExchangeError = NativeMessageExchangeError,
  Meta = undefined
> = ErrorExchangeMessageWithPayloadAndMeta<Type, CustomError, Meta>;

/**
 * A Exchange Error message with inferred requirements for the payload and metadata properties.
 * The `payload` and `meta` properties will be required if the corresponding type argument
 * if not the `undefined` type.
 *
 * Note: The `payload` property will always be required, since the `CustomError` type argument does
 * not allow for specification of the `undefined` type.
 */
export type ErrorExchangeMessageAuto<
  Type extends string = string,
  CustomError extends NativeMessageExchangeError = NativeMessageExchangeError,
  Meta = undefined
> = Meta extends undefined
  ? ErrorExchangeMessageWithPayload<Type, CustomError, Meta>
  : ErrorExchangeMessageWithPayloadAndMeta<Type, CustomError, Meta>;
/**
 * Alias for ErrorExchangeMessageAuto
 */
export type ErrorEMAuto<
  Type extends string = string,
  CustomError extends NativeMessageExchangeError = NativeMessageExchangeError,
  Meta = undefined
> = ErrorExchangeMessageAuto<Type, CustomError, Meta>;

/**
 * An error object
 */
export interface NativeMessageExchangeError {
  errorCode: string;
  errorDescription?: string;
}

function isValidKey(key: string): boolean {
  return ['type', 'payload', 'error', 'meta', 'txId'].indexOf(key) > -1;
}
